import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Server, Send, RotateCcw, Save } from "lucide-react";
import HtmlCodeEditor from "./HtmlCodeEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      <div className="mb-4 flex items-center gap-2.5">
        {Icon && <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF6A00]/12 text-[#FF6A00]"><Icon className="h-4.5 w-4.5" strokeWidth={1.7} /></span>}
        <p className="text-sm font-semibold text-white">{title}</p>
      </div>
      {children}
    </div>
  );
}

const F = ({ label, ...p }) => (
  <div><Label className="text-zinc-400">{label}</Label><Input {...p} className="mt-1 border-zinc-800 bg-zinc-950 text-white" /></div>
);

export default function Personalisation({ token, readOnly }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [smtp, setSmtp] = useState({ enabled: false, host: "", port: 465, username: "", password: "", from_email: "", from_name: "HalgarOS", secure: "ssl", password_set: false });
  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/superadmin/email-settings`, h).then(({ data }) => {
      setSmtp((s) => ({ ...s, ...(data.smtp || {}), password: "" }));
      setTemplates(data.templates || []);
    }).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const updateTpl = (patch) => setTemplates((arr) => arr.map((t, i) => (i === sel ? { ...t, ...patch } : t)));
  const t = templates[sel];

  const save = async () => {
    setBusy(true);
    try {
      const email_templates = {};
      templates.forEach((tp) => {
        if (tp.subject !== tp.default_subject || tp.html !== tp.default_html) {
          email_templates[tp.key] = { subject: tp.subject, html: tp.html };
        }
      });
      const smtpPayload = { enabled: smtp.enabled, host: smtp.host, port: Number(smtp.port) || 465, username: smtp.username, from_email: smtp.from_email, from_name: smtp.from_name, secure: smtp.secure };
      if (smtp.password) smtpPayload.password = smtp.password;
      const { data } = await axios.put(`${API}/superadmin/email-settings`, { email_templates, smtp: smtpPayload }, h);
      setSmtp((s) => ({ ...s, ...(data.smtp || {}), password: "" }));
      setTemplates(data.templates || []);
      toast.success("Personnalisation enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const testSmtp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/superadmin/email-settings/smtp-test`, { host: smtp.host, port: Number(smtp.port) || 465, username: smtp.username, password: smtp.password, from_email: smtp.from_email, secure: smtp.secure }, h);
      toast.success("Connexion SMTP réussie ✓");
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de connexion SMTP"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    const to = window.prompt("Adresse email pour recevoir le test :");
    if (!to) return;
    try {
      await save();
      await axios.post(`${API}/superadmin/email-settings/test`, { to, key: t?.key || "security_alert" }, h);
      toast.success(`Email de test envoyé à ${to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
  };

  const previewHtml = () => {
    const sample = { org_name: "Studio Exemple", when: "dans 3 jours", plan: "Pro · 79 €/mois", cta_url: "#", action: "Test d'envoi depuis Valhalla", email: "client@exemple.com" };
    return (t?.html || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (sample[k] != null ? sample[k] : ""));
  };

  return (
    <div className="space-y-5" data-testid="sa-personalisation">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-white">Personnalisation des emails</h1>
        {!readOnly && <Button data-testid="perso-save" onClick={save} disabled={busy} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>}
      </div>

      <Panel title="Serveur SMTP d'envoi des emails HalgarOS" icon={Server}>
        <label className="mb-4 flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-300">
          <span>Utiliser mon propre SMTP (sinon envoi via le service managé)</span>
          <Switch data-testid="smtp-enabled" checked={!!smtp.enabled} onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })} disabled={readOnly} />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <F label="Hôte SMTP" data-testid="smtp-host" value={smtp.host || ""} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.exemple.com" disabled={readOnly} />
          <F label="Port" data-testid="smtp-port" type="number" value={smtp.port || ""} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} placeholder="465" disabled={readOnly} />
          <F label="Utilisateur" data-testid="smtp-username" value={smtp.username || ""} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} disabled={readOnly} />
          <F label="Mot de passe" data-testid="smtp-password" type="password" value={smtp.password || ""} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.password_set ? "•••••••• (inchangé)" : ""} disabled={readOnly} />
          <F label="Email expéditeur (From)" data-testid="smtp-from-email" value={smtp.from_email || ""} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="hey@halgaros.com" disabled={readOnly} />
          <F label="Nom expéditeur" data-testid="smtp-from-name" value={smtp.from_name || ""} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} disabled={readOnly} />
          <div>
            <Label className="text-zinc-400">Sécurité</Label>
            <select data-testid="smtp-secure" value={smtp.secure || "ssl"} onChange={(e) => setSmtp({ ...smtp, secure: e.target.value })} disabled={readOnly} className="mt-1 h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-white">
              <option value="ssl">SSL/TLS (port 465)</option>
              <option value="starttls">STARTTLS (port 587)</option>
            </select>
          </div>
        </div>
        {!readOnly && <Button data-testid="smtp-test" onClick={testSmtp} disabled={busy} variant="outline" className="mt-4 rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Server className="mr-1.5 h-4 w-4" />Tester la connexion SMTP</Button>}
      </Panel>

      <Panel title="Modèles d'emails (HTML / CSS)" icon={Mail}>
        <div className="mb-4 flex flex-wrap gap-2" data-testid="tpl-tabs">
          {templates.map((tp, i) => (
            <button key={tp.key} data-testid={`tpl-tab-${tp.key}`} onClick={() => setSel(i)} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${i === sel ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:bg-zinc-800"}`}>
              {tp.label}{tp.is_custom ? " ●" : ""}
            </button>
          ))}
        </div>
        {t && (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <F label="Objet de l'email" data-testid="tpl-subject" value={t.subject || ""} onChange={(e) => updateTpl({ subject: e.target.value })} disabled={readOnly} />
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <Label className="text-zinc-400">Corps HTML</Label>
                  {!readOnly && <button data-testid="tpl-reset" onClick={() => updateTpl({ subject: t.default_subject, html: t.default_html })} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"><RotateCcw className="h-3 w-3" />Réinitialiser</button>}
                </div>
                <HtmlCodeEditor testId="tpl-html" value={t.html || ""} onChange={(v) => updateTpl({ html: v })} disabled={readOnly} />
              </div>
              <p className="text-xs text-zinc-500">Variables disponibles : {t.variables.map((v) => <code key={v} className="mx-0.5 rounded bg-zinc-800 px-1 text-zinc-300">{`{{${v}}}`}</code>)}</p>
              {!readOnly && <Button data-testid="tpl-test" onClick={sendTest} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Send className="mr-1.5 h-4 w-4" />Enregistrer & envoyer un test</Button>}
            </div>
            <div>
              <Label className="text-zinc-400">Aperçu</Label>
              <iframe data-testid="tpl-preview" title="preview" srcDoc={previewHtml()} className="mt-1 h-[26rem] w-full rounded-xl border border-zinc-800 bg-white" />
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
